import { AUTOSUR_COMPANY_ID } from 'lib/pdfs/autosur/valeResguardoAutosur';

/**
 * Custody vouchers per client, in one place (it replaces isAutosurCompany).
 *
 *  - individualFormat: the per-asset voucher of "Acciones" in the asset
 *    detail ("Exportar PDF" with and without price). 'generico' keeps the two
 *    voucher components of always (EmployeeAssetUnitario[Price]PDF); any other
 *    value exports the client's own format (lib/vouchers INDIVIDUAL_FORMATS).
 *  - signIndividual / signGeneral: "Firmar vale de resguardo" in "Acciones" and
 *    "Firmar y guardar" on the employee voucher of Reportes > Vales de resguardo.
 *
 * "Acciones" is never hidden (user's decision of 2026-11-02, replacing the
 * hideGenericActions of AutoSur and Círculo): it shows the client's format.
 *
 * Signing is OFF by default (user's decision of 2026-11-02): a signed voucher
 * can never be deleted from the asset, and several formats carry legal text the
 * client has to approve before it is signed electronically. A client gets it
 * by being listed here.
 *
 * A subcompany entry wins over a company entry. The custom formats of
 * EmployeesAssets[Price]PDF are keyed by subcompany, as their if/else is.
 */
export const VOUCHER_FORMATS = {
  GENERIC: 'generico',
  AUTOSUR: 'autosur',
  CIRCULO_CREDITO: 'circulo_credito',
  CIRCULO_LABORAL: 'circulo_laboral',
};

export const DEFAULT_VOUCHER_CONFIG = {
  individualFormat: VOUCHER_FORMATS.GENERIC,
  signIndividual: false,
  signGeneral: false,
};

// Formats that also exist with price ("Exportar PDF con precio").
export const FORMATS_WITH_PRICE = [
  VOUCHER_FORMATS.GENERIC,
  VOUCHER_FORMATS.CIRCULO_CREDITO,
  VOUCHER_FORMATS.CIRCULO_LABORAL,
];

export const CIRCULO_CREDITO_SUBCOMPANY_ID = '611d4d8d39e15e0020534f71';
export const CIRCULO_LABORAL_SUBCOMPANY_ID = '6226793be2d6650028b55175';

export const VOUCHER_CLIENTS = [
  {
    client: 'AutoSur',
    company: AUTOSUR_COMPANY_ID,
    individualFormat: VOUCHER_FORMATS.AUTOSUR,
    signIndividual: true,
    signGeneral: true, // user's decision of 2026-11-03: its format for all the assets
  },
  {
    client: 'Círculo de Crédito',
    subcompanies: [CIRCULO_CREDITO_SUBCOMPANY_ID],
    individualFormat: VOUCHER_FORMATS.CIRCULO_CREDITO,
    signIndividual: true,
    signGeneral: true,
  },
  {
    client: 'Círculo Laboral',
    subcompanies: [CIRCULO_LABORAL_SUBCOMPANY_ID],
    individualFormat: VOUCHER_FORMATS.CIRCULO_LABORAL,
    signIndividual: true,
    signGeneral: true,
  },
];

const idOf = (entity) => (entity && (entity.id || entity._id)) || null;

/**
 * @param {Object} company - {id}
 * @param {Object} subcompany - {id}
 * @param {Object[]} clients - the list (VOUCHER_CLIENTS unless a test passes one)
 * @returns {Object} {client, individualFormat, signIndividual, signGeneral}
 */
export const voucherConfig = (company, subcompany, clients = VOUCHER_CLIENTS) => {
  const companyId = idOf(company);
  const subcompanyId = idOf(subcompany);
  const entry =
    (subcompanyId &&
      clients.find((c) => (c.subcompanies || []).includes(String(subcompanyId)))) ||
    (companyId && clients.find((c) => c.company && c.company === String(companyId)));
  if (!entry) return { client: null, ...DEFAULT_VOUCHER_CONFIG };
  const { company: _c, subcompanies: _s, ...settings } = entry;
  return { ...DEFAULT_VOUCHER_CONFIG, ...settings };
};

/**
 * The "Acciones" menu of the asset detail, in order. Every voucher needs a
 * custodian (no custodian: no menu, as before).
 *  - 'generic' / 'genericPrice': the two components of always. A client
 *    without configuration gets exactly these, as before this branch.
 *  - 'export' / 'exportPrice': the client's own format, one asset ('exportPrice'
 *    only for formats in FORMATS_WITH_PRICE).
 *  - 'sign': "Firmar vale de resguardo", only with signIndividual.
 * @returns {{items: string[], format: string}}
 */
export const detailVoucherOptions = (company, subcompany, asset, clients) => {
  const config = voucherConfig(company, subcompany, clients);
  const format = config.individualFormat;
  if (!(asset && asset._employee)) return { items: [], format };
  const exportItems =
    format === VOUCHER_FORMATS.GENERIC
      ? ['generic', 'genericPrice']
      : ['export'].concat(FORMATS_WITH_PRICE.includes(format) ? ['exportPrice'] : []);
  return { items: exportItems.concat(config.signIndividual ? ['sign'] : []), format };
};

/** Whether Reportes > Vales de resguardo offers "Firmar y guardar" (stage D). */
export const canSignGeneralVoucher = (company, subcompany, clients) =>
  voucherConfig(company, subcompany, clients).signGeneral;



// WEBPACK FOOTER //
// src/lib/vouchers/clientConfig.js